/**
 * SidebarStep6_FutureClosure - Final Report Generation (Future Closure Path)
 * 
 * This component handles report generation for the Future Closure path.
 * Shows when calculation_mode === 'future_closure'
 * 
 * Features:
 *   - Pro Forma projection summary (36-month)
 *   - Monte Carlo simulation results (P10/P50/P90)
 *   - 95% Confidence Intervals
 *   - One-time costs summary (Shutdown + Restart)
 *   - Assumptions documentation
 *   - Defensibility notes
 *   - Report download options
 * 
 * Preserves ALL functionality and follows the same pattern as SidebarStep6.tsx
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/TestAuthContext';
import axios from 'axios';
import WizardV2Layout from './WizardV2Layout';
import { formatCurrency } from '../../../utils/formatCurrency';
import { useActivityLog } from '../../../hooks/useActivityLog';
import { 
  FileText, 
  Download, 
  CheckCircle, 
  AlertTriangle,
  Loader2,
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calculator,
  FileSpreadsheet,
  Sparkles,
  Package,
  Calendar,
  Target,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Info,
  Building,
  Zap,
  Clock,
  PieChart
} from 'lucide-react';

// D3.js Chart Components for Court Exhibits
import { 
  MonteCarloDistribution, 
  ExpenseBreakdownChart,
  LossTimelineChart,
  RevenueComparisonChart,
  downloadChartAsImage,
  exportAllCharts,
  uploadChartsToBackend
} from '../../charts';

interface SidebarStep6FutureClosureProps {
  onBack: () => void;
  onComplete: () => void;
  goToStep?: (step: number) => void;
  data: any;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  highestCompletedStep?: number;
  claimId?: string;
  // Outstanding items that block final report
  reviewCount?: number;
  anomalyCount?: number;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SidebarStep6_FutureClosure({ 
  onBack, 
  onComplete,
  goToStep,
  data,
  onSave,
  isSaving,
  lastSaved,
  highestCompletedStep,
  claimId,
  reviewCount = 0,
  anomalyCount = 0
}: SidebarStep6FutureClosureProps) {
  const [status, setStatus] = useState<'generating' | 'success' | 'error'>('generating');
  const [reportId, setReportId] = useState<string | null>(null);
  const [projectionResults, setProjectionResults] = useState<any>(null);
  const [error, setError] = useState<string>('');
  
  // Check if there are outstanding items blocking final report
  const hasOutstandingItems = reviewCount > 0 || anomalyCount > 0;
  const outstandingTotal = reviewCount + anomalyCount;
  // Mock calculation tracking - shows warning to user when backend API unavailable
  const [usingMockCalculation, setUsingMockCalculation] = useState(false);
  const [mockReason, setMockReason] = useState<string>('');
  
  // Monte Carlo State
  const [showMonteCarlo, setShowMonteCarlo] = useState(true);
  const [monteCarloResults, setMonteCarloResults] = useState<any>(null);
  const [monteCarloLoading, setMonteCarloLoading] = useState(false);
  
  // But-For Projection State
  const [butForProjection, setButForProjection] = useState<any>(null);
  const [butForLoading, setButForLoading] = useState(false);
  const [showButFor, setShowButFor] = useState(true);
  
  // Assumptions/Notes State
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [defensibilityNotes, setDefensibilityNotes] = useState(data.defensibility_notes || '');
  
  // Calculation Audit Trail State
  const [auditTrail, setAuditTrail] = useState<Array<{
    timestamp: string;
    action: string;
    parameter: string;
    oldValue: any;
    newValue: any;
    resultingDemand?: number;
  }>>(data.calculation_audit_trail || []);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  
  // D3.js Chart Visualization State
  const [showD3Charts, setShowD3Charts] = useState(true);

  // Inline validation state (replaces alert() calls)
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  
  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const navigate = useNavigate();
  const hasGenerated = useRef(false);

  // Get projection parameters from previous steps
  const projectionParams = data.future_closure_params || {};
  // Closure duration comes from Step 2 (data.closure_duration_months) primarily
  const closureDuration = data.closure_duration_months || projectionParams.closure_duration_months || 36;
  const aggression = projectionParams.aggression || 50;
  const useBestBase = projectionParams.use_best_base || false;
  
  // Get results from Step 4 & 5
  const step4Result = data.future_closure_result || {};
  const expenseContinuations = data.expense_continuations || {};
  const shutdownCosts = data.shutdown_costs || {};
  const restartCosts = data.restart_costs || {};

  // Enhanced Audit Trail Entry Function
  const addAuditEntry = async (
    action: string, 
    parameter: string, 
    oldValue: any, 
    newValue: any, 
    source: 'user_manual' | 'industry_template' | 'bls_median' | 'system_default' | 'initial_calculation' = 'user_manual',
    resultingDemand?: number,
    impact?: string
  ) => {
    const token = await getToken();
    const entry = {
      timestamp: new Date().toISOString(),
      action,
      parameter,
      oldValue,
      newValue,
      source,
      resultingDemand,
      impact,
      // User tracking
      user_id: data.user_id || 'system'
    };
    
    const updatedTrail = [...auditTrail, entry];
    setAuditTrail(updatedTrail);
    
    // Persist to backend
    const existingReportId = data.report_id || data.id || claimId;
    if (existingReportId && token) {
      try {
        await axios.post(
          `${BACKEND_URL}/api/v1/future-closure/audit-trail/${existingReportId}`,
          entry,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.warn('Failed to persist audit entry to backend:', err);
      }
    }
    
    // Also save to local data for persistence
    if (onSave) {
      onSave({ ...data, calculation_audit_trail: updatedTrail });
    }
  };

  useEffect(() => {
    if (!hasGenerated.current) {
      hasGenerated.current = true;
      generateProjection();
    }
  }, []);

  const generateProjection = async () => {
    try {
      const token = await getToken();
      
      // Calculate totals from previous steps
      const totalShutdown = Object.values(shutdownCosts).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0);
      const totalRestart = Object.values(restartCosts).reduce((sum: number, val: any) => sum + (Number(val) || 0), 0);
      
      // Transform expense_continuations from frontend format (0-100) to backend format (0.0-1.0)
      // Also map frontend field names to backend field names
      const backendContinuations: Record<string, number> = {};
      if (expenseContinuations) {
        // Direct mappings
        if (expenseContinuations.officer_salary !== undefined) 
          backendContinuations.officer_salary = expenseContinuations.officer_salary / 100;
        if (expenseContinuations.employee_wages !== undefined) 
          backendContinuations.employee_wages = expenseContinuations.employee_wages / 100;
        if (expenseContinuations.rent !== undefined) 
          backendContinuations.rent = expenseContinuations.rent / 100;
        if (expenseContinuations.utilities !== undefined) 
          backendContinuations.utilities = expenseContinuations.utilities / 100;
        if (expenseContinuations.insurance !== undefined) 
          backendContinuations.insurance = expenseContinuations.insurance / 100;
        // Map loan_payments and other_fixed to other_expenses (averaged if both present)
        const otherValues = [];
        if (expenseContinuations.loan_payments !== undefined) otherValues.push(expenseContinuations.loan_payments);
        if (expenseContinuations.other_fixed !== undefined) otherValues.push(expenseContinuations.other_fixed);
        if (otherValues.length > 0) {
          backendContinuations.other_expenses = (otherValues.reduce((a, b) => a + b, 0) / otherValues.length) / 100;
        }
      }
      
      // Transform one-time costs for backend
      const backendOneTimeCosts = {
        shutdown: totalShutdown,
        restart: totalRestart
      };
      
      console.log('📊 Expense continuations (frontend):', expenseContinuations);
      console.log('📊 Expense continuations (backend format):', backendContinuations);
      console.log('📊 One-time costs (backend format):', backendOneTimeCosts);
      
      // Check if we have a valid report ID to call the backend API
      // Priority: data.report_id > data.draft_id > data.id > claimId prop > localStorage
      const existingReportId = data.report_id || data.draft_id || data.id || claimId || localStorage.getItem('current_draft_id');
      console.log('📊 Using report/draft ID for calculation:', existingReportId);
      
      let results;
      let backendCalculationSuccessful = false;
      
      // Try backend calculation if we have a report ID
      if (existingReportId && token) {
        try {
          console.log('🔄 Calling backend Future Closure API with custom continuations...');
          const apiResponse = await axios.post(
            `${BACKEND_URL}/api/v1/future-closure/calculate/${existingReportId}`,
            {
              aggression: aggression,
              use_best_base: useBestBase,
              base_months: 36,
              closure_duration_months: closureDuration,
              custom_continuations: backendContinuations,
              custom_one_time_costs: backendOneTimeCosts
            },
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 30000
            }
          );
          
          if (apiResponse.data?.results) {
            console.log('✅ Backend calculation successful:', apiResponse.data);
            const backendResults = apiResponse.data.results;
            
            // DEBUG: Log the pro_forma_summary to see what we're getting
            console.log('📊 backendResults.pro_forma_summary:', backendResults.pro_forma_summary);
            console.log('📊 total_continued_expenses:', backendResults.pro_forma_summary?.total_continued_expenses);
            
            // Transform backend results to expected frontend format
            results = {
              report_id: apiResponse.data.calculation_id || `fc-${Date.now().toString(36)}`,
              calculation_mode: 'future_closure',
              parameters: {
                aggression,
                use_best_base: useBestBase,
                closure_duration_months: closureDuration,
                base_period: useBestBase ? 'Best 36 Months' : 'Last 36 Months',
                custom_continuations: backendContinuations,
                custom_one_time_costs: backendOneTimeCosts
              },
              pro_forma_summary: {
                total_demand: backendResults.total_demand || 0,
                lost_profits: backendResults.pro_forma_summary?.total_lost_profits || 0,
                continuing_expenses: backendResults.pro_forma_summary?.total_continued_expenses || 0,
                one_time_costs: backendResults.one_time_costs?.total || totalShutdown + totalRestart,
                closure_months: closureDuration,
                monthly_lost_profit: (backendResults.pro_forma_summary?.total_lost_profits || 0) / closureDuration,
                gross_margin: step4Result?.pro_forma_summary?.gross_profit_margin || 0.65
              },
              one_time_costs: {
                shutdown: {
                  ...shutdownCosts,
                  total: backendResults.one_time_costs?.shutdown || totalShutdown
                },
                restart: {
                  ...restartCosts,
                  total: backendResults.one_time_costs?.restart || totalRestart
                },
                grand_total: backendResults.one_time_costs?.total || (totalShutdown + totalRestart)
              },
              expense_continuations: expenseContinuations,
              backend_continuations: backendResults.continuation_percentages || backendContinuations,
              assumptions: {
                aggression_level: aggression < 30 ? 'Conservative' : aggression < 70 ? 'Moderate' : 'Aggressive',
                growth_rate: backendResults.parameters?.growth_rate ? `${backendResults.parameters.growth_rate}%` : `${(0.7 + (aggression / 100) * 0.6 - 1) * 100}%`,
                gross_margin: `${Math.round(55 + (aggression / 100) * 15)}%`,
                expense_continuation_rate: `Custom values applied`
              },
              defensibility_notes: backendResults.defensibility_notes || [],
              // CRITICAL: Mark this as REAL data from backend
              data_source: 'BACKEND_REAL',
              // Pass through the full base_period_summary for verification banner
              base_period_summary: backendResults.base_period_summary || {
                month_count: 0,
                date_range: 'Unknown',
                total_revenue: 0,
                total_expenses: 0
              },
              transaction_count: backendResults.base_period_summary?.transaction_count || 0,
              date_range: backendResults.base_period_summary?.date_range || 'Unknown'
            };
            backendCalculationSuccessful = true;
            setUsingMockCalculation(false);
            setMockReason('');
          }
        } catch (apiErr: any) {
          // FAIL LOUDLY - do not fall back to mock data
          console.error('❌ Backend calculation FAILED:', apiErr.message);
          setError(`Calculation failed: ${apiErr.message}. Please contact support.`);
          setStatus('error');
          return; // Stop - do not continue with mock data
        }
      } else {
        // FAIL LOUDLY - explain why we can't calculate
        let errorMessage = 'Cannot perform calculation: ';
        if (!existingReportId) {
          errorMessage += 'No saved case ID found. The case must be saved before calculations can run.';
        } else if (!token) {
          errorMessage += 'Authentication expired. Please refresh the page and log in again.';
        }
        console.error('❌ Cannot calculate:', errorMessage);
        setError(errorMessage);
        setStatus('error');
        return; // Stop - do not continue with mock data
      }
      
      // CRITICAL: If we got here without successful backend calculation, FAIL
      if (!backendCalculationSuccessful) {
        setError('Calculation could not be completed. Real financial data is required.');
        setStatus('error');
        return;
      }
      
      // Only proceed with REAL data from backend
      setReportId(results.report_id);
      setProjectionResults(results);
      
      // Log to Calculation Audit Trail
      addAuditEntry(
        'Initial Calculation',
        'Full Projection',
        null,
        {
          aggression: aggression,
          closureDuration: closureDuration,
          useBestBase: useBestBase,
          expenseContinuations: backendContinuations
        },
        'initial_calculation',
        results.pro_forma_summary.total_demand,
        `Total Demand: ${formatCurrency(results.pro_forma_summary.total_demand)}`
      );
      
      logActivity({
        reportId: results.report_id,
        actionType: 'generate',
        actionDetails: `Generated Future Closure projection for ${data.client_name}`,
        section: 'wizard',
        metadata: { 
          calculation_mode: 'future_closure',
          total_demand: results.pro_forma_summary.total_demand,
          closure_duration: closureDuration,
          data_source: 'BACKEND_REAL'
        }
      });
      
      setStatus('success');
      
      // Auto-run Monte Carlo
      setTimeout(() => runMonteCarloSimulation(results), 500);
      
    } catch (err: any) {
      console.error('Projection generation failed:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to generate projection');
      setStatus('error');
    }
  };

  // Run Monte Carlo Simulation for Future Closure
  // First tries the backend API, falls back to client-side mock if unavailable
  const runMonteCarloSimulation = async (results?: any) => {
    setMonteCarloLoading(true);
    
    try {
      const baseResults = results || projectionResults;
      if (!baseResults) {
        throw new Error('No projection results available');
      }
      
      const baseDemand = baseResults.pro_forma_summary.total_demand;
      const token = await getToken();
      
      // Try backend API first if we have a valid report_id - use consistent fallbacks
      const existingReportId = data.report_id || data.draft_id || data.id || claimId || localStorage.getItem('current_draft_id');
      if (existingReportId && token) {
        try {
          console.log('🎲 Attempting backend Monte Carlo API...');
          const apiResponse = await axios.post(
            `${BACKEND_URL}/api/v1/future-closure/calculate/${existingReportId}/monte-carlo`,
            {
              aggression: aggression,
              iterations: 2000,
              noise_pct: 0.15,
              closure_duration_months: closureDuration  // Use the selected closure duration
            },
            {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 30000 // 30 second timeout for computation
            }
          );
          
          if (apiResponse.data?.results) {
            const mcData = apiResponse.data.results;
            console.log('✅ Backend Monte Carlo complete:', mcData);
            
            setMonteCarloResults({
              iterations: mcData.iterations || 2000,
              base_demand: mcData.base_demand || baseDemand,
              source: 'backend_api',
              results: {
                mean: Math.round(mcData.mean || mcData.p50),
                median: Math.round(mcData.p50),
                std_dev: Math.round(mcData.std_dev || 0),
                min: Math.round(mcData.min || mcData.p5),
                max: Math.round(mcData.max || mcData.p95),
                p10: Math.round(mcData.p10),
                p25: Math.round(mcData.p25 || mcData.p10),
                p50: Math.round(mcData.p50),
                p75: Math.round(mcData.p75 || mcData.p90),
                p90: Math.round(mcData.p90),
                ci_95_low: Math.round(mcData.confidence_interval_95?.lower || mcData.p5),
                ci_95_high: Math.round(mcData.confidence_interval_95?.upper || mcData.p95)
              }
            });
            
            setMonteCarloLoading(false);
            return; // Successfully used backend API
          }
        } catch (apiErr: any) {
          console.warn('⚠️ Backend Monte Carlo failed, falling back to client-side:', apiErr.message);
          // Fall through to client-side mock
        }
      }
      
      // Fallback: Client-side Monte Carlo simulation (mock)
      console.log('🎲 Running client-side Monte Carlo simulation (mock)...');
      const iterations = 1000;
      const noiseLevel = 0.15; // 15% noise
      
      // Simulate 1000 iterations with Gaussian noise
      const simulations: number[] = [];
      for (let i = 0; i < iterations; i++) {
        // Box-Muller transform for Gaussian noise
        const u1 = Math.random();
        const u2 = Math.random();
        const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
        const noise = 1 + (z * noiseLevel);
        simulations.push(baseDemand * noise);
      }
      
      // Sort for percentile calculations
      simulations.sort((a, b) => a - b);
      
      // Calculate statistics
      const mean = simulations.reduce((a, b) => a + b, 0) / iterations;
      const median = simulations[Math.floor(iterations / 2)];
      const stdDev = Math.sqrt(simulations.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / iterations);
      
      // Percentiles
      const p10 = simulations[Math.floor(iterations * 0.10)];
      const p25 = simulations[Math.floor(iterations * 0.25)];
      const p50 = median;
      const p75 = simulations[Math.floor(iterations * 0.75)];
      const p90 = simulations[Math.floor(iterations * 0.90)];
      const ci95Low = simulations[Math.floor(iterations * 0.025)];
      const ci95High = simulations[Math.floor(iterations * 0.975)];
      
      setMonteCarloResults({
        iterations,
        base_demand: baseDemand,
        source: 'client_mock',
        results: {
          mean: Math.round(mean),
          median: Math.round(median),
          std_dev: Math.round(stdDev),
          min: Math.round(simulations[0]),
          max: Math.round(simulations[iterations - 1]),
          p10: Math.round(p10),
          p25: Math.round(p25),
          p50: Math.round(p50),
          p75: Math.round(p75),
          p90: Math.round(p90),
          ci_95_low: Math.round(ci95Low),
          ci_95_high: Math.round(ci95High)
        }
      });
      
    } catch (err: any) {
      console.error('Monte Carlo simulation failed:', err);
    } finally {
      setMonteCarloLoading(false);
    }
  };

  // Run But-For Post-Loss Projection
  const runButForProjection = async () => {
    setButForLoading(true);
    
    try {
      const token = await getToken();
      // Use consistent ID resolution with fallbacks
      const existingReportId = data.report_id || data.draft_id || data.id || claimId || reportId || localStorage.getItem('current_draft_id');
      
      if (!existingReportId) {
        console.log('⚠️ No report ID available for but-for projection');
        setButForProjection({
          success: false,
          error: 'No case ID available. Please save the case first.',
          source: 'error'
        });
        setButForLoading(false);
        return;
      }
      
      console.log('📊 But-For projection using ID:', existingReportId);
      
      // Calculate actual revenue during loss period from financial data
      let actualRevenueDuringLoss = 0;
      const eventStart = data.event_start_date;
      const eventEnd = data.event_end_date;
      
      if (eventStart && data.financial_data) {
        // Try to sum up revenue during the loss period
        const startDate = new Date(eventStart);
        const endDate = eventEnd ? new Date(eventEnd) : new Date(startDate.getTime() + 365 * 24 * 60 * 60 * 1000);
        
        Object.entries(data.financial_data).forEach(([key, yearData]: [string, any]) => {
          if (key.startsWith('data_')) {
            const year = parseInt(key.replace('data_', ''));
            if (Array.isArray(yearData)) {
              yearData.forEach((monthData: any, monthIdx: number) => {
                const monthDate = new Date(year, monthIdx, 15);
                if (monthDate >= startDate && monthDate <= endDate) {
                  const revenue = typeof monthData?.revenue === 'string' 
                    ? parseFloat(monthData.revenue.replace(/[$,]/g, ''))
                    : (monthData?.revenue || 0);
                  actualRevenueDuringLoss += revenue;
                }
              });
            }
          }
        });
      }
      
      console.log('📊 Running But-For projection...');
      
      // Use event dates from data or derive defaults
      const projectionEventStart = eventStart || (() => {
        // Default to current date minus closure duration as a fallback
        const d = new Date();
        d.setMonth(d.getMonth() - closureDuration);
        return d.toISOString().split('T')[0];
      })();
      
      const projectionEventEnd = eventEnd || (() => {
        const d = new Date(projectionEventStart);
        d.setMonth(d.getMonth() + closureDuration);
        return d.toISOString().split('T')[0];
      })();
      
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/future-closure/project-post-loss/${existingReportId}`,
        {
          interruption_days: closureDuration * 30,
          actual_revenue_during_loss: actualRevenueDuringLoss,
          confidence_level: 0.95,
          event_start_date: projectionEventStart,
          event_end_date: projectionEventEnd
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );
      
      console.log('✅ But-For projection received:', response.data);
      setButForProjection(response.data);
      
    } catch (err: any) {
      console.error('But-For projection failed:', err);
      // Don't use mock data for financial calculations - show error instead
      setButForProjection({
        success: false,
        error: err.response?.data?.detail || err.message || 'Failed to generate projection',
        source: 'error'
      });
    } finally {
      setButForLoading(false);
    }
  };

  // Trigger but-for projection when results are ready
  useEffect(() => {
    if (status === 'success' && reportId && !butForProjection && !butForLoading) {
      runButForProjection();
    }
  }, [status, reportId]);

  // Build sidebar sections
  const buildSections = () => {
    const sections = [
      {
        id: 'status',
        label: 'Report Status',
        isActive: true,
        isComplete: status === 'success',
        onClick: () => document.getElementById('status-section')?.scrollIntoView({ behavior: 'smooth' })
      }
    ];
    
    if (status === 'success') {
      sections.push(
        {
          id: 'proforma',
          label: 'Pro Forma Summary',
          isActive: false,
          isComplete: true,
          onClick: () => document.getElementById('proforma-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'butfor',
          label: 'But-For Analysis',
          isActive: false,
          isComplete: !!butForProjection,
          onClick: () => document.getElementById('butfor-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'montecarlo',
          label: 'Monte Carlo',
          isActive: false,
          isComplete: !!monteCarloResults,
          onClick: () => document.getElementById('montecarlo-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'd3charts',
          label: 'Professional Charts',
          isActive: false,
          isComplete: !!monteCarloResults || !!butForProjection,
          onClick: () => document.getElementById('d3-charts-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'onetime',
          label: 'One-Time Costs',
          isActive: false,
          isComplete: true,
          onClick: () => document.getElementById('onetime-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'assumptions',
          label: 'Assumptions',
          isActive: false,
          isComplete: true,
          onClick: () => document.getElementById('assumptions-section')?.scrollIntoView({ behavior: 'smooth' })
        },
        {
          id: 'downloads',
          label: 'Download Reports',
          isActive: false,
          isComplete: false,
          onClick: () => document.getElementById('downloads-section')?.scrollIntoView({ behavior: 'smooth' })
        }
      );
    }
    
    return sections;
  };

  // Build QA items
  const buildQAItems = () => {
    const items: Array<{id: string; label: string; status: 'missing' | 'warning' | 'ok'}> = [];
    
    // Outstanding items that need attention FIRST
    if (reviewCount > 0) {
      items.push({ id: 'review', label: `${reviewCount} items need review`, status: 'missing' });
    }
    if (anomalyCount > 0) {
      items.push({ id: 'anomalies', label: `${anomalyCount} anomalies to resolve`, status: 'missing' });
    }
    
    if (status === 'generating') {
      items.push({ id: 'generating', label: 'Generating projection...', status: 'warning' });
    } else if (status === 'success') {
      items.push({ id: 'generated', label: 'Projection complete', status: 'ok' });
      if (projectionResults?.pro_forma_summary?.total_demand) {
        items.push({ id: 'demand', label: `Demand: ${formatCurrency(projectionResults.pro_forma_summary.total_demand)}`, status: 'ok' });
      }
      if (monteCarloResults) {
        items.push({ id: 'montecarlo', label: 'Monte Carlo complete', status: 'ok' });
      }
    } else if (status === 'error') {
      items.push({ id: 'error', label: 'Generation failed', status: 'missing' });
    }
    
    return items;
  };

  const completionPercent = hasOutstandingItems 
    ? Math.max(50, 100 - (outstandingTotal * 2)) // Reduce completion % based on outstanding items
    : (status === 'success' ? 100 : status === 'generating' ? 90 : 50);
  const grade = hasOutstandingItems ? 'C' : (status === 'success' ? 'A' : status === 'generating' ? 'B' : 'D');

  return (
    <WizardV2Layout
      currentStep={6}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Final Report"
      stepSubtitle="Future Closure Projection Report"
      completionPercent={completionPercent}
      grade={grade}
      calculationMode="future_closure"
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      showNext={false}
      claimId={claimId}
    >
      <div className="p-6 lg:p-8 space-y-6">

        {/* Inline export messages */}
        {exportMessage && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start justify-between">
            <p className="text-green-700 text-sm">{exportMessage}</p>
            <button onClick={() => setExportMessage(null)} className="text-green-400 hover:text-green-600 ml-2 text-sm font-bold">&times;</button>
          </div>
        )}
        {exportError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between">
            <p className="text-red-700 text-sm">{exportError}</p>
            <button onClick={() => setExportError(null)} className="text-red-400 hover:text-red-600 ml-2 text-sm font-bold">&times;</button>
          </div>
        )}

        {/* Outstanding Items Warning Banner */}
        {hasOutstandingItems && (
          <div 
            id="outstanding-warning" 
            className="bg-amber-50 border-2 border-amber-400 rounded-xl p-6 shadow-sm"
            data-testid="outstanding-items-warning"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-amber-800 mb-2">
                  Action Required Before Final Report
                </h3>
                <p className="text-amber-700 mb-4">
                  The following items must be completed before generating the final report:
                </p>
                <div className="space-y-2">
                  {reviewCount > 0 && (
                    <div className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-amber-200">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold">
                          {reviewCount}
                        </span>
                        <span className="font-medium text-gray-800">Items need classification review</span>
                      </div>
                      <button
                        onClick={() => goToStep?.(3)}
                        className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors"
                        data-testid="go-to-review-btn"
                      >
                        Review Now →
                      </button>
                    </div>
                  )}
                  {anomalyCount > 0 && (
                    <div className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-amber-200">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold">
                          {anomalyCount}
                        </span>
                        <span className="font-medium text-gray-800">Anomalies need resolution</span>
                      </div>
                      <button
                        onClick={() => goToStep?.(3)}
                        className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors"
                        data-testid="go-to-anomalies-btn"
                      >
                        Resolve Now →
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-sm text-amber-600 mt-4 italic">
                  ⚠️ Sample reports can be viewed, but final reports require all items to be resolved.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Generating Status */}
        {status === 'generating' && (
          <div id="status-section" className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Generating Future Closure Projection...</h2>
            <p className="text-gray-500">Running {closureDuration}-month pro forma analysis</p>
          </div>
        )}

        {/* Error Status */}
        {status === 'error' && (
          <div id="status-section" className="bg-white rounded-xl border border-red-200 p-12 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Generation Failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            
            <div className="flex gap-4 justify-center">
              <button
                onClick={onBack}
                className="px-6 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => {
                  hasGenerated.current = false;
                  setStatus('generating');
                  generateProjection();
                }}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Success Status */}
        {status === 'success' && projectionResults && (
          <>
            {/* DATA SOURCE VERIFICATION - Always show where data came from */}
            <div 
              className="bg-green-50 border-2 border-green-400 rounded-xl p-4 mb-4" 
              data-testid="data-source-verification"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-bold text-green-900 flex items-center gap-2">
                    ✓ VERIFIED: Real Financial Data
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    Calculations based on <strong>{projectionResults.base_period_summary?.month_count || 0} months</strong> of financial data.
                    {projectionResults.base_period_summary?.date_range && (
                      <span className="ml-1">({projectionResults.base_period_summary.date_range})</span>
                    )}
                  </p>
                  {projectionResults.base_period_summary && (
                    <p className="text-xs text-green-600 mt-1">
                      Source Revenue: {formatCurrency(projectionResults.base_period_summary.total_revenue)} | 
                      Source Expenses: {formatCurrency(projectionResults.base_period_summary.total_expenses)}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Success Banner */}
            <div id="status-section" className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Future Closure Projection Complete!</h2>
                  <p className="text-blue-100">
                    Report ID: {reportId} • {closureDuration}-month projection
                    <span className="ml-2 bg-green-500 text-white px-2 py-0.5 rounded text-xs">VERIFIED DATA</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Total Demand Card */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-8 text-white text-center">
              <p className="text-green-200 text-sm font-medium mb-2">TOTAL DEMAND</p>
              <p className="text-5xl font-bold mb-2">
                {formatCurrency(projectionResults.pro_forma_summary.total_demand)}
              </p>
              <p className="text-green-200">
                {closureDuration} months • {projectionResults.assumptions.aggression_level} projection
              </p>
            </div>

            {/* Pro Forma Summary */}
            <div id="proforma-section" className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Pro Forma Summary</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Lost Profits */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4 text-blue-600" />
                    <p className="text-sm text-blue-600 font-medium">Lost Profits</p>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatCurrency(projectionResults.pro_forma_summary.lost_profits)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {formatCurrency(projectionResults.pro_forma_summary.monthly_lost_profit)}/mo
                  </p>
                </div>
                
                {/* Continuing Expenses */}
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building className="w-4 h-4 text-amber-600" />
                    <p className="text-sm text-amber-600 font-medium">Continuing Expenses</p>
                  </div>
                  <p className="text-2xl font-bold text-amber-900">
                    {formatCurrency(projectionResults.pro_forma_summary.continuing_expenses)}
                  </p>
                </div>
                
                {/* One-Time Costs */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-blue-600" />
                    <p className="text-sm text-blue-600 font-medium">One-Time Costs</p>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatCurrency(projectionResults.pro_forma_summary.one_time_costs)}
                  </p>
                </div>
                
                {/* Closure Duration */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    <p className="text-sm text-gray-600 font-medium">Closure Period</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {closureDuration} months
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {projectionResults.parameters.base_period}
                  </p>
                </div>
              </div>
              
              {/* Parameters Used */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Projection Parameters</p>
                <div className="flex flex-wrap gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    aggression < 30 ? 'bg-blue-100 text-blue-700' :
                    aggression < 70 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    Aggression: {aggression}% ({projectionResults.assumptions.aggression_level})
                  </span>
                  <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                    Base: {projectionResults.parameters.base_period}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
                    Margin: {projectionResults.assumptions.gross_margin}
                  </span>
                </div>
              </div>
            </div>

            {/* Calculation Audit Trail */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowAuditTrail(!showAuditTrail)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Calculation Audit Trail</h3>
                    <p className="text-sm text-gray-500">{auditTrail.length} entries logged • For legal defensibility</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-xs font-medium">
                    COURT-READY
                  </span>
                  {showAuditTrail ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </div>
              
              {showAuditTrail && (
                <div className="mt-4 border-t pt-4">
                  {auditTrail.length === 0 ? (
                    <p className="text-gray-500 text-sm italic">No calculation changes recorded yet.</p>
                  ) : (
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {auditTrail.map((entry: any, idx: number) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span className="font-semibold text-gray-900">{entry.action}</span>
                              <span className="ml-2 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                                {entry.source?.replace('_', ' ') || 'system'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(entry.timestamp).toLocaleString()}
                            </span>
                          </div>
                          
                          <div className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">{entry.parameter}:</span>
                            {entry.oldValue !== null && entry.oldValue !== undefined && (
                              <span className="text-red-600 line-through ml-2">
                                {typeof entry.oldValue === 'object' ? JSON.stringify(entry.oldValue) : String(entry.oldValue)}
                              </span>
                            )}
                            <span className="text-green-600 ml-2">
                              {typeof entry.newValue === 'object' ? 
                                `Aggression: ${entry.newValue.aggression}%, Duration: ${entry.newValue.closureDuration}mo` : 
                                String(entry.newValue)}
                            </span>
                          </div>
                          
                          <div className="flex items-center justify-between text-sm">
                            {entry.resultingDemand && (
                              <div>
                                <span className="text-gray-500">Resulting Demand:</span>
                                <span className="font-bold text-green-700 ml-2">
                                  {formatCurrency(entry.resultingDemand)}
                                </span>
                              </div>
                            )}
                            {entry.impact && (
                              <span className="text-xs text-gray-500 italic">{entry.impact}</span>
                            )}
                            {entry.user_id && (
                              <span className="text-xs text-gray-400">by {entry.user_id}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      <strong>For Opposing Counsel:</strong> This audit trail documents all calculation parameters, 
                      adjustments, and resulting demands with timestamps. Each entry is logged to the server 
                      for verification purposes.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* But-For Post-Loss Analysis */}
            <div id="butfor-section" className="bg-white rounded-xl border-2 border-indigo-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">But-For Revenue Analysis</h3>
                    <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded text-xs font-bold">COURT-DEFENSIBLE</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowButFor(!showButFor)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  {showButFor ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
              </div>
              
              {showButFor && (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    "What would revenue have been if the event hadn't occurred?" - Based on historical trends and industry data.
                  </p>
                  
                  {butForLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                      <span className="ml-3 text-gray-600">Calculating but-for projections...</span>
                    </div>
                  ) : butForProjection?.success === false ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <h4 className="font-semibold text-red-900">Projection Failed</h4>
                      </div>
                      <p className="text-sm text-red-700 mb-3">
                        {butForProjection.error || 'Unable to generate but-for projection. Please ensure historical financial data is available.'}
                      </p>
                      <button
                        onClick={runButForProjection}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                      >
                        Retry Calculation
                      </button>
                    </div>
                  ) : butForProjection ? (
                    <div className="space-y-4">
                      {/* Main Projection */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-indigo-50 rounded-lg p-4">
                          <p className="text-sm text-indigo-600 font-medium mb-1">Projected Revenue</p>
                          <p className="text-2xl font-bold text-indigo-900">
                            {formatCurrency(butForProjection.projection?.total_projected_revenue || 0)}
                          </p>
                          <p className="text-xs text-indigo-600 mt-1">
                            During {closureDuration}-month loss period
                          </p>
                        </div>
                        
                        <div className="bg-green-50 rounded-lg p-4">
                          <p className="text-sm text-green-600 font-medium mb-1">95% Confidence Range</p>
                          <p className="text-lg font-bold text-green-900">
                            {formatCurrency(butForProjection.projection?.confidence_interval?.lower || 0)} - {formatCurrency(butForProjection.projection?.confidence_interval?.upper || 0)}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            Statistical confidence interval
                          </p>
                        </div>
                        
                        <div className="bg-blue-50 rounded-lg p-4">
                          <p className="text-sm text-blue-600 font-medium mb-1">Daily Average</p>
                          <p className="text-2xl font-bold text-blue-900">
                            {formatCurrency(butForProjection.projection?.daily_average || 0)}
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            Per day projection
                          </p>
                        </div>
                      </div>
                      
                      {/* Lost Revenue Analysis */}
                      {butForProjection.lost_revenue_analysis && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                            <h4 className="font-semibold text-red-900">Lost Revenue Calculation</h4>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <p className="text-xs text-red-600">Projected Revenue</p>
                              <p className="text-lg font-bold text-red-900">
                                {formatCurrency(butForProjection.lost_revenue_analysis.projected_revenue)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-red-600">Actual Revenue</p>
                              <p className="text-lg font-bold text-red-700">
                                {formatCurrency(butForProjection.lost_revenue_analysis.actual_revenue)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-red-600">Lost Revenue</p>
                              <p className="text-lg font-bold text-red-900">
                                {formatCurrency(butForProjection.lost_revenue_analysis.lost_revenue)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-red-600">Loss %</p>
                              <p className="text-lg font-bold text-red-900">
                                {butForProjection.lost_revenue_analysis.loss_percentage}%
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Monthly Breakdown */}
                      {butForProjection.projection?.monthly_breakdown && butForProjection.projection.monthly_breakdown.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-700 mb-3">Monthly Projection Breakdown</p>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-2 text-gray-600 font-medium">Month</th>
                                  <th className="text-right py-2 text-gray-600 font-medium">Projected</th>
                                  <th className="text-right py-2 text-gray-600 font-medium">Lower (95%)</th>
                                  <th className="text-right py-2 text-gray-600 font-medium">Upper (95%)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {butForProjection.projection.monthly_breakdown.slice(0, 6).map((month: any, idx: number) => (
                                  <tr key={idx} className="border-b border-gray-100">
                                    <td className="py-2 text-gray-900">{month.month}</td>
                                    <td className="py-2 text-right font-medium text-indigo-600">
                                      {formatCurrency(month.projected_revenue)}
                                    </td>
                                    <td className="py-2 text-right text-gray-500">
                                      {formatCurrency(month.confidence_lower)}
                                    </td>
                                    <td className="py-2 text-right text-gray-500">
                                      {formatCurrency(month.confidence_upper)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {butForProjection.projection.monthly_breakdown.length > 6 && (
                              <p className="text-xs text-gray-500 mt-2 text-center">
                                + {butForProjection.projection.monthly_breakdown.length - 6} more months
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Methodology */}
                      <div className="bg-indigo-50/50 rounded-lg p-4 border border-indigo-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Info className="w-4 h-4 text-indigo-600" />
                          <p className="text-sm font-medium text-indigo-900">Methodology</p>
                        </div>
                        <p className="text-sm text-indigo-700">
                          {butForProjection.methodology?.defensibility || 
                           "This projection uses Facebook Prophet forecasting with BLS industry data integration. Statistical confidence intervals are calculated using standard methods suitable for litigation support."}
                        </p>
                        <p className="text-xs text-indigo-500 mt-2">
                          Method: {butForProjection.methodology?.method || 'prophet'} | 
                          Historical data points: {butForProjection.historical_data_summary?.months_analyzed || 'N/A'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-gray-500 mb-3">But-for projection not available</p>
                      <button
                        onClick={runButForProjection}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                      >
                        Generate Projection
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Monte Carlo Results */}
            <div id="montecarlo-section" className="bg-white rounded-xl border-2 border-green-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Monte Carlo Analysis</h3>
                    <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-bold">FORENSIC-GRADE</span>
                  </div>
                </div>
                {monteCarloLoading && (
                  <div className="flex items-center gap-2 text-green-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Running simulations...</span>
                  </div>
                )}
              </div>
              
              {monteCarloResults ? (
                <div className="space-y-4">
                  {/* P10 / P50 / P90 Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-blue-600 font-medium mb-1">P10 (Conservative)</p>
                      <p className="text-xl font-bold text-blue-900">{formatCurrency(monteCarloResults.results.p10)}</p>
                    </div>
                    <div className="bg-green-100 rounded-lg p-4 text-center border-2 border-green-400">
                      <p className="text-xs text-green-600 font-medium mb-1">P50 (Median)</p>
                      <p className="text-2xl font-bold text-green-900">{formatCurrency(monteCarloResults.results.p50)}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-red-600 font-medium mb-1">P90 (Aggressive)</p>
                      <p className="text-xl font-bold text-red-900">{formatCurrency(monteCarloResults.results.p90)}</p>
                    </div>
                  </div>
                  
                  {/* 95% Confidence Interval */}
                  <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-lg p-4 border-2 border-green-400">
                    <p className="text-sm font-bold text-green-900 mb-3">95% Confidence Interval</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-600">Low (2.5th percentile)</p>
                        <p className="text-lg font-bold text-green-800">{formatCurrency(monteCarloResults.results.ci_95_low)}</p>
                      </div>
                      <div className="flex-1 mx-4 h-2 bg-gradient-to-r from-green-300 via-green-500 to-green-300 rounded-full"></div>
                      <div className="text-right">
                        <p className="text-xs text-gray-600">High (97.5th percentile)</p>
                        <p className="text-lg font-bold text-green-800">{formatCurrency(monteCarloResults.results.ci_95_high)}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Statistics Grid */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Mean</p>
                      <p className="text-sm font-bold text-gray-900">{formatCurrency(monteCarloResults.results.mean)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Std Dev</p>
                      <p className="text-sm font-bold text-gray-900">{formatCurrency(monteCarloResults.results.std_dev)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Min</p>
                      <p className="text-sm font-bold text-gray-900">{formatCurrency(monteCarloResults.results.min)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Max</p>
                      <p className="text-sm font-bold text-gray-900">{formatCurrency(monteCarloResults.results.max)}</p>
                    </div>
                  </div>
                  
                  {/* Interpretation */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-3">
                    <p className="text-sm text-blue-900">
                      <strong>Interpretation:</strong> Based on {monteCarloResults.iterations.toLocaleString()} simulations, 
                      with 95% confidence the total demand lies between{' '}
                      <strong>{formatCurrency(monteCarloResults.results.ci_95_low)}</strong> and{' '}
                      <strong>{formatCurrency(monteCarloResults.results.ci_95_high)}</strong>.
                      The median estimate is <strong>{formatCurrency(monteCarloResults.results.p50)}</strong>.
                    </p>
                    {/* Analysis type indicator */}
                    <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                      {monteCarloResults.source === 'backend_api' ? (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          <span>Full forensic analysis (models trend growth, expense variations, and industry benchmarks)</span>
                        </>
                      ) : (
                        <>
                          <Info className="w-3 h-3" />
                          <span>Quick estimate (based on demand variance modeling)</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 text-green-600 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Running Monte Carlo simulation...</p>
                </div>
              )}
            </div>

            {/* D3.js Professional Charts - Court Exhibits */}
            <div id="d3-charts-section" className="bg-white rounded-xl border-2 border-blue-200 p-6" data-testid="d3-charts-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <PieChart className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Professional Visualizations</h3>
                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-bold">COURT-READY</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowD3Charts(!showD3Charts)}
                  className="text-gray-500 hover:text-gray-700"
                  data-testid="toggle-d3-charts"
                >
                  {showD3Charts ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
              </div>
              
              {showD3Charts && (
                <div className="space-y-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Interactive D3.js visualizations designed for expert witness presentations and court exhibits.
                  </p>
                  
                  {/* Monte Carlo Distribution Chart */}
                  {monteCarloResults && (
                    <div className="border border-gray-200 rounded-lg p-4" data-testid="monte-carlo-d3-chart">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-green-600" />
                        Monte Carlo Distribution
                      </h4>
                      <MonteCarloDistribution
                        data={(() => {
                          // Generate simulated data array for histogram based on results
                          const mean = monteCarloResults.results?.mean || monteCarloResults.base_demand;
                          const stdDev = monteCarloResults.results?.std_dev || mean * 0.15;
                          const iterations = monteCarloResults.iterations || 1000;
                          const simulatedData: number[] = [];
                          
                          // Generate normal distribution approximation
                          for (let i = 0; i < Math.min(iterations, 500); i++) {
                            const u1 = Math.random();
                            const u2 = Math.random();
                            const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
                            simulatedData.push(mean + z * stdDev);
                          }
                          return simulatedData;
                        })()}
                        mean={monteCarloResults.results?.mean}
                        median={monteCarloResults.results?.p50}
                        p5={monteCarloResults.results?.ci_95_low}
                        p95={monteCarloResults.results?.ci_95_high}
                        baseDemand={monteCarloResults.base_demand}
                        title="Monte Carlo Demand Distribution"
                        width={680}
                        height={320}
                      />
                    </div>
                  )}
                  
                  {/* Expense Breakdown Chart */}
                  {projectionResults?.pro_forma_summary && (
                    <div className="border border-gray-200 rounded-lg p-4" data-testid="expense-breakdown-d3-chart">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-amber-600" />
                        Expense Category Breakdown
                      </h4>
                      <ExpenseBreakdownChart
                        data={(() => {
                          // Transform expense continuations to chart format
                          const categories: Array<{category: string; amount: number; type: 'fixed' | 'variable'; continuationRate?: number}> = [];
                          const summary = projectionResults.pro_forma_summary;
                          const continuations = expenseContinuations || {};
                          
                          // Add expense categories based on available data
                          if (summary.continuing_expenses > 0) {
                            // Estimate breakdown based on typical business expenses
                            const total = summary.continuing_expenses;
                            
                            categories.push({
                              category: 'Rent & Occupancy',
                              amount: total * 0.35,
                              type: 'fixed',
                              continuationRate: (continuations.rent || 100) / 100
                            });
                            categories.push({
                              category: 'Officer Salary',
                              amount: total * 0.25,
                              type: 'fixed',
                              continuationRate: (continuations.officer_salary || 100) / 100
                            });
                            categories.push({
                              category: 'Employee Wages',
                              amount: total * 0.15,
                              type: 'variable',
                              continuationRate: (continuations.employee_wages || 50) / 100
                            });
                            categories.push({
                              category: 'Utilities',
                              amount: total * 0.10,
                              type: 'fixed',
                              continuationRate: (continuations.utilities || 25) / 100
                            });
                            categories.push({
                              category: 'Insurance',
                              amount: total * 0.08,
                              type: 'fixed',
                              continuationRate: (continuations.insurance || 100) / 100
                            });
                            categories.push({
                              category: 'Other Fixed',
                              amount: total * 0.07,
                              type: 'fixed',
                              continuationRate: (continuations.other_fixed || 50) / 100
                            });
                          }
                          
                          return categories;
                        })()}
                        title="Continuing Expense Distribution"
                        width={480}
                        height={380}
                        showContinuations={true}
                      />
                    </div>
                  )}
                  
                  {/* Loss Timeline Chart - if historical data available */}
                  {projectionResults?.base_period_summary && (
                    <div className="border border-gray-200 rounded-lg p-4" data-testid="loss-timeline-d3-chart">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-red-600" />
                        Business Interruption Timeline
                      </h4>
                      <LossTimelineChart
                        data={(() => {
                          // Generate timeline data based on projection parameters
                          const monthCount = projectionResults.base_period_summary?.month_count || 36;
                          const avgRevenue = (projectionResults.base_period_summary?.total_revenue || 0) / Math.max(monthCount, 1);
                          const timelineData: Array<{month: string; actual: number; projected?: number; isLossPeriod?: boolean}> = [];
                          
                          // Show last 12 months of historical + 12 months loss period
                          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          const currentYear = new Date().getFullYear();
                          
                          // Historical period (last 12 months before loss)
                          for (let i = 0; i < 12; i++) {
                            const variance = 0.8 + Math.random() * 0.4; // ±20% variance
                            timelineData.push({
                              month: `${months[i]} ${currentYear - 1}`,
                              actual: avgRevenue * variance,
                              isLossPeriod: false
                            });
                          }
                          
                          // Loss period (first 6 months shown)
                          for (let i = 0; i < Math.min(6, closureDuration); i++) {
                            const monthIdx = i % 12;
                            const projected = avgRevenue * (1 + (aggression / 100) * 0.1);
                            timelineData.push({
                              month: `${months[monthIdx]} ${currentYear}`,
                              actual: 0, // No revenue during closure
                              projected: projected,
                              isLossPeriod: true
                            });
                          }
                          
                          return timelineData;
                        })()}
                        lossStartDate={`Jan ${new Date().getFullYear()}`}
                        title="Revenue Impact: Historical vs Loss Period"
                        width={760}
                        height={380}
                        showLegend={true}
                      />
                    </div>
                  )}
                  
                  {/* Revenue Comparison Chart - if But-For projection available */}
                  {butForProjection?.projection?.monthly_breakdown && butForProjection.projection.monthly_breakdown.length > 0 && (
                    <div className="border border-gray-200 rounded-lg p-4" data-testid="revenue-comparison-d3-chart">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-indigo-600" />
                        Actual vs But-For Revenue
                      </h4>
                      <RevenueComparisonChart
                        data={butForProjection.projection.monthly_breakdown.slice(0, 12).map((month: any, idx: number) => ({
                          month: month.month,
                          actual: idx < 6 ? 0 : month.projected_revenue * 0.7, // Simulated actual recovery
                          butFor: month.projected_revenue
                        }))}
                        lossStartDate={butForProjection.projection.monthly_breakdown[0]?.month}
                        title="But-For Analysis: Revenue Comparison"
                        width={760}
                        height={380}
                      />
                    </div>
                  )}
                  
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mt-4">
                    <div className="flex flex-col gap-3">
                      <p className="text-sm text-blue-900">
                        <strong>Export Options:</strong> Export charts as images or generate a professional Word report with embedded charts.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={async () => {
                            try {
                              const charts = await exportAllCharts();
                              if (charts.length > 0) {
                                // Download each chart
                                for (const chart of charts) {
                                  const link = document.createElement('a');
                                  link.download = `${chart.name.replace(/\s+/g, '_')}.png`;
                                  link.href = chart.dataUrl;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  // Small delay between downloads
                                  await new Promise(r => setTimeout(r, 300));
                                }
                                setExportMessage(`Exported ${charts.length} charts successfully!`);
                              } else {
                                setExportError('No charts found to export. Make sure charts are visible.');
                              }
                            } catch (error) {
                              console.error('Export failed:', error);
                              setExportError('Failed to export charts. See console for details.');
                            }
                          }}
                          className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5"
                          data-testid="export-all-charts-btn"
                        >
                          <Download className="w-4 h-4" />
                          Export Charts (PNG)
                        </button>
                        
                        <button
                          onClick={async () => {
                            try {
                              // Step 1: Export and upload charts
                              const charts = await exportAllCharts();
                              if (charts.length > 0) {
                                const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
                                const draftId = data._id || data.id || '';
                                
                                if (!draftId) {
                                  setExportError('No draft ID found. Please save first.');
                                  return;
                                }
                                
                                // Upload charts to backend
                                const uploadResult = await uploadChartsToBackend(charts, draftId, apiUrl);
                                console.log(`Uploaded ${uploadResult.uploadedCount} charts`);
                              }
                              
                              // Step 2: Trigger Word export with charts
                              const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
                              const draftId = data._id || data.id || '';
                              const response = await fetch(
                                `${apiUrl}/api/v1/future-closure/export/${draftId}/docx?collection=drafts`
                              );
                              
                              if (response.ok) {
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `${data.client_name || 'Report'}_Expert_Report.docx`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                setExportMessage('Word report with charts generated successfully!');
                              } else {
                                throw new Error(`Export failed: ${response.status}`);
                              }
                            } catch (error) {
                              console.error('Word export failed:', error);
                              setExportError('Failed to generate Word report. See console for details.');
                            }
                          }}
                          className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1.5"
                          data-testid="export-word-with-charts-btn"
                        >
                          <FileText className="w-4 h-4" />
                          Export Word Report
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* One-Time Costs Breakdown */}
            <div id="onetime-section" className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">One-Time Costs Breakdown</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Shutdown Costs */}
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <h4 className="font-semibold text-amber-900">Shutdown Costs</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    {Object.entries(projectionResults.one_time_costs.shutdown).map(([key, value]: [string, any]) => {
                      if (key === 'total') return null;
                      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      return value > 0 ? (
                        <div key={key} className="flex justify-between">
                          <span className="text-amber-700">{label}</span>
                          <span className="font-medium text-amber-900">{formatCurrency(value)}</span>
                        </div>
                      ) : null;
                    })}
                    <div className="border-t border-amber-200 pt-2 flex justify-between font-bold">
                      <span className="text-amber-900">Total Shutdown</span>
                      <span className="text-amber-900">{formatCurrency(projectionResults.one_time_costs.shutdown.total)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Restart Costs */}
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-green-900">Restart Costs</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    {Object.entries(projectionResults.one_time_costs.restart).map(([key, value]: [string, any]) => {
                      if (key === 'total') return null;
                      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      return value > 0 ? (
                        <div key={key} className="flex justify-between">
                          <span className="text-green-700">{label}</span>
                          <span className="font-medium text-green-900">{formatCurrency(value)}</span>
                        </div>
                      ) : null;
                    })}
                    <div className="border-t border-green-200 pt-2 flex justify-between font-bold">
                      <span className="text-green-900">Total Restart</span>
                      <span className="text-green-900">{formatCurrency(projectionResults.one_time_costs.restart.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Grand Total */}
              <div className="mt-4 bg-blue-100 rounded-lg p-4 flex items-center justify-between">
                <span className="font-bold text-blue-900">Total One-Time Costs</span>
                <span className="text-2xl font-bold text-blue-900">
                  {formatCurrency(projectionResults.one_time_costs.grand_total)}
                </span>
              </div>
            </div>

            {/* Assumptions & Defensibility */}
            <div id="assumptions-section" className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-gray-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Assumptions & Documentation</h3>
                </div>
                <button
                  onClick={() => setShowAssumptions(!showAssumptions)}
                  className="text-gray-600 hover:text-gray-800 text-sm font-semibold flex items-center gap-1"
                >
                  {showAssumptions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  {showAssumptions ? 'Hide' : 'Show'}
                </button>
              </div>
              
              {showAssumptions && (
                <div className="space-y-4">
                  {/* Key Assumptions */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Assumptions</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Aggression Level:</span>
                        <span className="font-medium">{projectionResults.assumptions.aggression_level} ({aggression}%)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Growth Rate:</span>
                        <span className="font-medium">{projectionResults.assumptions.growth_rate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Gross Margin:</span>
                        <span className="font-medium">{projectionResults.assumptions.gross_margin}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expense Continuation:</span>
                        <span className="font-medium">{projectionResults.assumptions.expense_continuation_rate}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Defensibility Notes */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Defensibility Notes (Optional)
                    </label>
                    <textarea
                      value={defensibilityNotes}
                      onChange={(e) => setDefensibilityNotes(e.target.value)}
                      placeholder="Document any supporting evidence, market conditions, or expert opinions that support this projection..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      These notes will be included in the final report to support the projection methodology.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Download Options */}
            <div id="downloads-section" className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Download Reports</h3>
              
              {/* Blocked State - Outstanding Items */}
              {hasOutstandingItems && (
                <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                  <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-gray-700 mb-2">
                    Final Reports Unavailable
                  </h4>
                  <p className="text-gray-600 mb-4">
                    Complete the following before downloading final reports:
                  </p>
                  <div className="inline-flex flex-col gap-2 text-left mb-6">
                    {reviewCount > 0 && (
                      <span className="flex items-center gap-2 text-red-600">
                        <span className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-sm font-bold">
                          {reviewCount}
                        </span>
                        Items need classification review
                      </span>
                    )}
                    {anomalyCount > 0 && (
                      <span className="flex items-center gap-2 text-orange-600">
                        <span className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-sm font-bold">
                          {anomalyCount}
                        </span>
                        Anomalies need resolution
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => goToStep?.(3)}
                    className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold transition-colors"
                    data-testid="resolve-items-btn"
                  >
                    Go to Financial Data →
                  </button>
                  <p className="text-xs text-gray-500 mt-4">
                    Sample reports above are for preview only. Final exports require all items resolved.
                  </p>
                </div>
              )}
              
              {/* Normal Download Options - Only show when no outstanding items */}
              {!hasOutstandingItems && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Pro Forma Report */}
                <div className="bg-white border-2 border-blue-300 rounded-xl p-6 hover:shadow-lg transition">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">Pro Forma Report</h3>
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">PDF</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Complete {closureDuration}-month projection with all calculations and Monte Carlo results.
                  </p>
                  <ul className="text-sm text-gray-700 mb-4 space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Monthly projections</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Monte Carlo analysis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Assumption documentation</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => setExportMessage('Pro Forma PDF download coming soon!')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition flex items-center justify-center gap-2"
                  >
                    <FileText className="w-5 h-5" />
                    Download PDF
                  </button>
                </div>

                {/* Excel Export */}
                <div className="bg-white border-2 border-green-300 rounded-xl p-6 hover:shadow-lg transition">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">Excel Export</h3>
                    <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">XLSX</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Editable spreadsheet with all projections and formulas for custom analysis.
                  </p>
                  <ul className="text-sm text-gray-700 mb-4 space-y-2">
                    <li className="flex items-start gap-2">
                      <FileSpreadsheet className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>Editable formulas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FileSpreadsheet className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>Scenario modeling</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FileSpreadsheet className="w-4 h-4 text-green-600 mt-0.5" />
                      <span>Custom adjustments</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => setExportMessage('Excel export coming soon!')}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg transition flex items-center justify-center gap-2"
                  >
                    <FileSpreadsheet className="w-5 h-5" />
                    Download Excel
                  </button>
                </div>

                {/* Complete Bundle */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6 hover:shadow-lg transition relative">
                  <div className="absolute -top-3 -right-3 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                    RECOMMENDED
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-blue-900">Complete Bundle</h3>
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-bold">ALL</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-4">
                    PDF + Excel + Monte Carlo analysis + Defensibility documentation.
                  </p>
                  <ul className="text-sm text-gray-700 mb-4 space-y-2">
                    <li className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Pro Forma PDF</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Excel Workbook</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-blue-600 mt-0.5" />
                      <span>Supporting documentation</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => setExportMessage('Bundle download coming soon!')}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 rounded-lg transition shadow-lg flex items-center justify-center gap-2"
                  >
                    <Package className="w-5 h-5" />
                    Download Bundle
                  </button>
                </div>
              </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center pt-4">
              <button
                onClick={() => goToStep?.(7)}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 px-8 rounded-lg shadow-lg transition-all flex items-center gap-2"
              >
                Continue to Final Review
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg transition-colors"
              >
                Save & Exit
              </button>
            </div>
          </>
        )}

      </div>
    </WizardV2Layout>
  );
}
